import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bookmark,
  Eye,
  Filter,
  GripVertical,
  Loader2,
  Mail,
  Send,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  apercuSegmentation,
  enregistrerSegment,
  envoyerCampagne,
  fetchSegmentation,
  previsualiserCourriel,
  supprimerSegment,
  type CritereChoisi,
  type CritereSegment,
  type ReglageCritere,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Écrire à une sélection.
 *
 * CE QUE CET ÉCRAN AJOUTE À « ÉCRIRE À UN GROUPE »
 * ---------------------------------------------------------------------------
 * L'autre écran écrit à cinq listes toutes faites : le bureau, les bénévoles,
 * les adhérents, les partenaires, la lettre d'information. C'est ce qu'il faut
 * la plupart du temps, et il reste.
 *
 * Ce qu'il ne sait pas faire, c'est « les familles qui doivent encore plus de
 * cinq euros pour la saison en cours ». Le trésorier le faisait à la main :
 * export du fichier, tri dans un tableur, copie des adresses. Une heure, et
 * une lettre identique pour tout le monde parce que personnaliser cinquante
 * courriels à la main n'est pas raisonnable.
 *
 * LE COMPTE S'AFFICHE AVANT, PAS APRÈS
 * ---------------------------------------------------------------------------
 * Chaque changement de critère redemande le nombre de destinataires. C'est le
 * seul moyen de comprendre ce qu'on vient de sélectionner : « 84 » puis « 3 »
 * après avoir ajouté un critère dit tout de suite qu'on s'est trompé, alors
 * qu'on ne s'en apercevrait jamais en lisant la liste des critères.
 *
 * ET LA PRÉVISUALISATION SE FAIT SUR UNE VRAIE FICHE
 * ---------------------------------------------------------------------------
 * Un aperçu avec des valeurs d'exemple est joli et ne prouve rien. Sur une
 * vraie fiche, on voit que la moitié du fichier n'a pas de date d'échéance —
 * c'est-à-dire qu'on voit la phrase telle qu'elle arrivera.
 */
export default function CampagnePage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peut = aLeDroit('diffuser_messages');

  const [criteres, setCriteres] = useState<CritereChoisi[]>([]);
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [modele, setModele] = useState('');
  const [segment, setSegment] = useState<number | undefined>();
  const [confirme, setConfirme] = useState(false);
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);
  const [exemple, setExemple] = useState<number | undefined>();
  const [glisse, setGlisse] = useState<string | null>(null);
  const [nomSegment, setNomSegment] = useState('');

  const champSujet = useRef<HTMLInputElement | null>(null);
  const champCorps = useRef<HTMLTextAreaElement | null>(null);
  /*
   * Le dernier champ touché, pour savoir où poser un clic sur la palette.
   *
   * Le glisser-déposer dit où il tombe ; le clic, non. Sans cette mémoire, un
   * clic sur « Prénom » irait toujours dans le corps, y compris quand on vient
   * d'écrire dans le sujet — et l'on ne comprendrait pas pourquoi le champ
   * n'apparaît pas là où on l'attend.
   */
  const dernierChamp = useRef<'sujet' | 'corps'>('corps');

  const { data, isPending } = useQuery({
    queryKey: ['segmentation'],
    queryFn: fetchSegmentation,
    enabled: peut,
  });

  /* ---------------------------- Le compte en direct ------------------------ */

  /*
   * Un délai avant de redemander.
   *
   * Régler un seuil à « 25 » frappe cinq touches, donc cinq requêtes dont
   * quatre portent sur des nombres qu'on n'a pas voulus — « 2 », « 25 »… Sur
   * une base d'adhérents, chacune est une requête avec jointures. On attend
   * donc que la main s'arrête.
   */
  const [criteresPoses, setCriteresPoses] = useState<CritereChoisi[]>([]);

  useEffect(() => {
    const minuteur = setTimeout(() => setCriteresPoses(criteres), 400);
    return () => clearTimeout(minuteur);
  }, [criteres]);

  const { data: apercu, isFetching: compte } = useQuery({
    queryKey: ['segmentation-apercu', criteresPoses],
    queryFn: () => apercuSegmentation(criteresPoses),
    enabled: peut && criteresPoses.length > 0,
  });

  const nombre = apercu?.apercu?.joignables ?? 0;

  /* ------------------------------ Les critères ----------------------------- */

  /*
   * Le catalogue, mémorisé plutôt que reconstruit à chaque rendu.
   *
   * `data?.criteres ?? []` fabrique un tableau NEUF à chaque passage quand la
   * réponse n'est pas encore là. Le `useMemo` qui range les critères par
   * famille le voyait donc changer sans arrêt et se recalculait toujours : un
   * mémo qui ne mémorise rien, et qui coûte le tri en plus.
   */
  const catalogue = useMemo(() => data?.criteres ?? [], [data]);

  const ajouterCritere = (definition: CritereSegment) => {
    if (criteres.some((c) => c.code === definition.code)) {
      return;
    }

    const reglages: Record<string, unknown> = {};
    for (const reglage of definition.reglages) {
      reglages[reglage.code] = reglage.defaut ?? (reglage.type === 'liste' ? (reglage.valeurs?.[0] ?? '') : 0);
    }

    setCriteres((v) => [...v, { code: definition.code, reglages }]);
    setConfirme(false);
  };

  const retirerCritere = (code: string) => {
    setCriteres((v) => v.filter((c) => c.code !== code));
    setConfirme(false);
  };

  const reglerCritere = (code: string, cle: string, valeur: unknown) => {
    setCriteres((v) =>
      v.map((c) => (c.code === code ? { ...c, reglages: { ...c.reglages, [cle]: valeur } } : c))
    );
    setConfirme(false);
  };

  const parFamille = useMemo(() => {
    const groupes = new Map<string, CritereSegment[]>();
    for (const critere of catalogue) {
      groupes.set(critere.famille, [...(groupes.get(critere.famille) ?? []), critere]);
    }
    return [...groupes.entries()];
  }, [catalogue]);

  /* ------------------------- La palette de champs -------------------------- */

  /**
   * Pose un champ à l'endroit exact où on l'a lâché.
   *
   * `caretPositionFromPoint` donne le rang du caractère sous le curseur. Sans
   * lui, le champ irait à la fin du texte, et il faudrait le déplacer à la
   * main — ce qui rend le glisser-déposer plus long que la frappe.
   */
  const poser = useCallback(
    (cible: 'sujet' | 'corps', jeton: string, evenement?: React.DragEvent) => {
      const champ = cible === 'sujet' ? champSujet.current : champCorps.current;
      if (!champ) return;

      const texte = champ.value;
      let position = champ.selectionStart ?? texte.length;

      if (evenement) {
        const doc = document as Document & {
          caretPositionFromPoint?: (x: number, y: number) => { offset: number } | null;
          caretRangeFromPoint?: (x: number, y: number) => Range | null;
        };
        const point =
          doc.caretPositionFromPoint?.(evenement.clientX, evenement.clientY) ??
          doc.caretRangeFromPoint?.(evenement.clientX, evenement.clientY);
        if (point) {
          const trouve = 'offset' in point ? point.offset : (point as Range).startOffset;
          if (typeof trouve === 'number' && trouve <= texte.length) {
            position = trouve;
          }
        }
      }

      const rendu = `${texte.slice(0, position)}${jeton}${texte.slice(position)}`;
      if (cible === 'sujet') setSujet(rendu);
      else setCorps(rendu);
      setConfirme(false);

      // Le curseur reste derrière ce qu'on vient de poser : on continue à
      // écrire là où on en était, sans avoir à recliquer.
      requestAnimationFrame(() => {
        champ.focus();
        const apres = position + jeton.length;
        champ.setSelectionRange(apres, apres);
      });
    },
    []
  );

  const surDepot = (cible: 'sujet' | 'corps') => (e: React.DragEvent) => {
    const jeton = e.dataTransfer.getData('text/plain');
    if (!jeton.startsWith('{{')) return;
    e.preventDefault();
    poser(cible, jeton, e);
    setGlisse(null);
  };

  /* ------------------------------- Le modèle -------------------------------- */

  const choisirModele = (code: string) => {
    const trouve = (data?.modeles ?? []).find((m) => m.code === code);
    if (!trouve) return;

    setModele(code);
    setSujet(trouve.sujet);
    setCorps(trouve.corps);
    setConfirme(false);

    /*
     * Les critères suggérés remplacent la sélection en cours.
     *
     * Les ajouter à ceux qui sont là donnerait des combinaisons qu'on n'a pas
     * voulues — « en défaut de paiement » ET « convoqués samedi » ne désigne
     * personne, et l'on chercherait longtemps pourquoi le compte tombe à zéro.
     */
    if (trouve.criteresSuggeres.length > 0) {
      setCriteres(trouve.criteresSuggeres.map((c) => ({ code: c.code, reglages: { ...c.reglages } })));
    }
  };

  /* ------------------------ Prévisualiser et envoyer ------------------------ */

  const previsualiser = useMutation({
    mutationFn: () => previsualiserCourriel({ criteres, sujet, corps, adherent: exemple }),
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const envoyer = useMutation({
    mutationFn: () => envoyerCampagne({ criteres, sujet, corps, modele, segment }),
    onSuccess: (r) => {
      setNotice({ type: 'ok', texte: r.message });
      setSujet('');
      setCorps('');
      setCriteres([]);
      setModele('');
      setConfirme(false);
      previsualiser.reset();
      queryClient.invalidateQueries({ queryKey: ['diffusions'] });
      queryClient.invalidateQueries({ queryKey: ['segmentation'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const garder = useMutation({
    mutationFn: () =>
      enregistrerSegment({ nom: nomSegment, criteres, modele: modele || undefined }),
    onSuccess: () => {
      setNomSegment('');
      setNotice({ type: 'ok', texte: 'Sélection enregistrée : elle se retrouve en haut de l\'écran.' });
      queryClient.invalidateQueries({ queryKey: ['segmentation'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const oublier = useMutation({
    mutationFn: (id: number) => supprimerSegment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['segmentation'] }),
  });

  /* --------------------------------- Rendu --------------------------------- */

  if (authLoading || (peut && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peut) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Écrire à une sélection dépend de votre fonction au bureau : un administrateur peut
              l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const pret =
    sujet.trim() !== '' && corps.trim() !== '' && criteres.length > 0 && nombre > 0 && confirme;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Filter className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Écrire à une sélection
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Choisissez qui, puis quoi. Chaque lettre part remplie avec les données de son
            destinataire.
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">
            Fermer
          </button>
        </div>
      )}

      {/* ---- 1. Les sélections déjà enregistrées ---- */}

      {(data?.segments ?? []).length > 0 && (
        <section className="card space-y-2">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Sélections enregistrées
          </p>
          <div className="flex flex-wrap gap-2">
            {(data?.segments ?? []).map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 rounded-full border-2 border-gray-200 dark:border-gray-700 pl-3 pr-1 py-1"
              >
                <button
                  type="button"
                  onClick={() => {
                    setCriteres(s.criteres.map((c) => ({ code: c.code, reglages: { ...c.reglages } })));
                    setSegment(s.id);
                    if (s.modele) choisirModele(s.modele);
                    setConfirme(false);
                  }}
                  className="text-sm text-gray-900 dark:text-gray-100"
                >
                  {s.nom}
                </button>
                <button
                  type="button"
                  onClick={() => oublier.mutate(s.id)}
                  aria-label={`Oublier « ${s.nom} »`}
                  className="p-1 text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ---- 2. À qui ---- */}

      <section className="card space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />À qui
          </h2>
          <ChiffreDestinataires
            criteres={criteres.length}
            enCours={compte}
            joignables={apercu?.apercu?.joignables ?? 0}
            total={apercu?.apercu?.total ?? 0}
            sansAdresse={apercu?.apercu?.sansAdresse ?? 0}
          />
        </div>

        {criteres.length > 0 && (
          <div className="space-y-2">
            {criteres.map((choisi) => {
              const definition = catalogue.find((c) => c.code === choisi.code);
              if (!definition) return null;

              return (
                <div
                  key={choisi.code}
                  className="rounded-xl border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 p-3 space-y-2"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {definition.libelle}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {definition.explication}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => retirerCritere(choisi.code)}
                      aria-label={`Retirer « ${definition.libelle} »`}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {definition.reglages.length > 0 && (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {definition.reglages.map((reglage) => (
                        <ChampReglage
                          key={reglage.code}
                          reglage={reglage}
                          valeur={choisi.reglages[reglage.code]}
                          onChange={(v) => reglerCritere(choisi.code, reglage.code, v)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Ajouter un critère
          </p>
          {parFamille.map(([famille, liste]) => (
            <div key={famille}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">{famille}</p>
              <div className="flex flex-wrap gap-2">
                {liste.map((critere) => {
                  const pose = criteres.some((c) => c.code === critere.code);
                  return (
                    <button
                      key={critere.code}
                      type="button"
                      disabled={pose}
                      onClick={() => ajouterCritere(critere)}
                      title={critere.explication}
                      className={`text-sm rounded-lg border-2 px-3 py-1.5 transition-colors ${
                        pose
                          ? 'border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
                          : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 hover:border-primary-300'
                      }`}
                    >
                      {critere.libelle}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {(apercu?.apercu?.inconnus ?? []).length > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Critère(s) inconnu(s), ignoré(s) : {(apercu?.apercu?.inconnus ?? []).join(', ')}.
          </p>
        )}

        {criteres.length > 0 && (
          <div className="flex items-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <label className="text-xs text-gray-500 dark:text-gray-400" htmlFor="nom-segment">
                Garder cette sélection pour la prochaine fois
              </label>
              <input
                id="nom-segment"
                value={nomSegment}
                onChange={(e) => setNomSegment(e.target.value)}
                placeholder="Retards de cotisation, seniors"
                className="input w-full"
              />
            </div>
            <button
              type="button"
              disabled={nomSegment.trim() === '' || garder.isPending}
              onClick={() => garder.mutate()}
              className="btn-secondary flex items-center gap-2 shrink-0"
            >
              <Bookmark className="w-4 h-4" />
              Garder
            </button>
          </div>
        )}
      </section>

      {/* ---- 3. Quel message ---- */}

      <section className="card space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary-600" />
          Quel message
        </h2>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.modeles ?? []).map((m) => (
            <button
              key={m.code}
              type="button"
              onClick={() => choisirModele(m.code)}
              className={`text-left rounded-xl border-2 p-3 transition-colors ${
                modele === m.code
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
              }`}
            >
              <p className="font-medium text-gray-900 dark:text-gray-100">{m.libelle}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{m.quand}</p>
            </button>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
          <div className="space-y-3 order-2 lg:order-1">
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Objet
              </label>
              <input
                ref={champSujet}
                value={sujet}
                onChange={(e) => {
                  setSujet(e.target.value);
                  setConfirme(false);
                }}
                onFocus={() => {
                  dernierChamp.current = 'sujet';
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={surDepot('sujet')}
                placeholder="Cotisation {{ licence.saison }} — il reste {{ cotisation.reste }}"
                className="input w-full"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Message
              </label>
              <textarea
                ref={champCorps}
                value={corps}
                onChange={(e) => {
                  setCorps(e.target.value);
                  setConfirme(false);
                }}
                onFocus={() => {
                  dernierChamp.current = 'corps';
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={surDepot('corps')}
                rows={14}
                placeholder="Bonjour {{ adherent.prenom }},"
                className="input w-full font-mono text-sm"
              />
            </div>
          </div>

          {/* La palette : on la glisse dans l'objet ou dans le message, ou
              l'on clique — le glisser-déposer est le geste le plus direct, il
              ne doit pas être le seul : il n'existe pas au doigt. */}
          <div className="order-1 lg:order-2 space-y-3">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Champs à déposer
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Glissez-en un dans l'objet ou dans le message. Un clic le pose là où était le
              curseur.
            </p>

            <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
              {(data?.variables ?? []).map((famille) => (
                <div key={famille.famille}>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {famille.libelle}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {famille.champs.map((champ) => {
                      const jeton = `{{ ${champ.cle} }}`;
                      return (
                        <button
                          key={champ.cle}
                          type="button"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', jeton);
                            e.dataTransfer.effectAllowed = 'copy';
                            setGlisse(champ.cle);
                          }}
                          onDragEnd={() => setGlisse(null)}
                          onClick={() => poser(dernierChamp.current, jeton)}
                          title={
                            champ.exigence
                              ? `${champ.exemple} — ${champ.exigence}`
                              : `Exemple : ${champ.exemple}`
                          }
                          className={`inline-flex items-center gap-1 text-xs rounded-lg border-2 border-gray-200 dark:border-gray-700 px-2 py-1 cursor-grab active:cursor-grabbing hover:border-primary-300 text-gray-900 dark:text-gray-100 ${
                            glisse === champ.cle ? 'opacity-40' : ''
                          }`}
                        >
                          <GripVertical className="w-3 h-3 text-gray-400 shrink-0" />
                          {champ.libelle}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---- 4. Vérifier, puis envoyer ---- */}

      <section className="card space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary-600" />
          Vérifier avant d'envoyer
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={sujet.trim() === '' || corps.trim() === '' || criteres.length === 0 || previsualiser.isPending}
            onClick={() => previsualiser.mutate()}
            className="btn-secondary flex items-center gap-2"
          >
            {previsualiser.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Voir la lettre telle qu'elle arrivera
          </button>

          {(apercu?.apercu?.exemples ?? []).length > 1 && (
            <select
              value={exemple ?? ''}
              onChange={(e) => {
                setExemple(e.target.value === '' ? undefined : Number(e.target.value));
                previsualiser.reset();
              }}
              className="input"
            >
              <option value="">Le premier de la sélection</option>
              {(apercu?.apercu?.exemples ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nom}
                </option>
              ))}
            </select>
          )}
        </div>

        {previsualiser.data?.apercu && (
          <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              Pour {previsualiser.data.apercu.destinataire.nom} —{' '}
              {previsualiser.data.apercu.destinataire.courriel}
            </div>
            <div className="p-3 space-y-2">
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {previsualiser.data.apercu.sujet}
              </p>
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {previsualiser.data.apercu.corps}
              </p>
            </div>
            {previsualiser.data.apercu.erreur && (
              <p className="bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm px-3 py-2 border-t border-amber-200">
                {previsualiser.data.apercu.erreur}
              </p>
            )}
          </div>
        )}

        {previsualiser.data && !previsualiser.data.apercu && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{previsualiser.data.message}</p>
        )}

        <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
          <input
            type="checkbox"
            checked={confirme}
            onChange={(e) => setConfirme(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            J'ai relu la lettre, et j'écris bien aux{' '}
            <strong className="tabular-nums">{nombre}</strong> destinataire
            {nombre > 1 ? 's' : ''} de cette sélection.
          </span>
        </label>

        <button
          type="button"
          disabled={!pret || envoyer.isPending}
          onClick={() => envoyer.mutate()}
          className="btn-primary flex items-center gap-2"
        >
          {envoyer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Mettre en file
        </button>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Les lettres ne partent pas d'un bloc : elles s'écoulent par vagues, sur quelques
          minutes. C'est voulu — l'hébergeur refuse en bloc cinq cents envois d'un coup.
        </p>
      </section>
    </div>
  );
}

/**
 * Le compte, et ce qui manque au compte.
 *
 * « 84 » seul ne dit pas grand-chose ; « 71 joignables sur 84 » dit qu'il
 * reste treize familles à prévenir autrement, ce qui est une information dont
 * on fait quelque chose.
 */
function ChiffreDestinataires({
  criteres,
  enCours,
  joignables,
  total,
  sansAdresse,
}: {
  criteres: number;
  enCours: boolean;
  joignables: number;
  total: number;
  sansAdresse: number;
}) {
  if (criteres === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Aucun critère : personne n'est sélectionné.
      </p>
    );
  }

  if (enCours) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        On compte…
      </p>
    );
  }

  return (
    <p className="text-sm text-gray-700 dark:text-gray-300">
      <strong className="text-lg tabular-nums text-primary-700 dark:text-primary-300">
        {joignables}
      </strong>{' '}
      destinataire{joignables > 1 ? 's' : ''}
      {sansAdresse > 0 && (
        <span className="text-gray-500 dark:text-gray-400">
          {' '}
          — sur {total}, {sansAdresse} sans adresse courriel
        </span>
      )}
    </p>
  );
}

/** Un réglage : un nombre, ou un choix dans une liste. */
function ChampReglage({
  reglage,
  valeur,
  onChange,
}: {
  reglage: ReglageCritere;
  valeur: unknown;
  onChange: (v: unknown) => void;
}) {
  if (reglage.type === 'liste') {
    return (
      <label className="text-xs text-gray-600 dark:text-gray-400">
        {reglage.libelle}
        <select
          value={String(valeur ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="input w-full mt-0.5"
        >
          {(reglage.valeurs ?? []).length === 0 && <option value="">Aucun choix disponible</option>}
          {(reglage.valeurs ?? []).map((v) => (
            <option key={v} value={v}>
              {/* La valeur voyage sous « 12|Judo » : on n'affiche que le nom. */}
              {v.includes('|') ? v.slice(v.indexOf('|') + 1) : v}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="text-xs text-gray-600 dark:text-gray-400">
      {reglage.libelle}
      <input
        type="number"
        value={String(valeur ?? '')}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="input w-full mt-0.5"
      />
    </label>
  );
}

